import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import clsx from "clsx";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import AudioRecorder from "../../helpers/audioRecorder";

let recorder = null;

const initRecorder = async () => {
  if (!recorder) {
    recorder = new AudioRecorder();
  }
  return recorder;
};

// Icons
const AttachIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
  </svg>
);

const EmojiIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SendIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const MicIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const CloseIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CancelIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const SpinnerIcon = ({ className }) => (
  <svg className={clsx("animate-spin", className)} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

const MessageInput = ({ ticketStatus }) => {
  const { ticketId } = useParams();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const { setReplyingMessage, replyingMessage } = useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);

  useEffect(() => {
    inputRef.current?.focus();
  }, [replyingMessage]);

  useEffect(() => {
    inputRef.current?.focus();
    return () => {
      setInputMessage("");
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  const handleChangeInput = (e) => {
    setInputMessage(e.target.value);
    handleLoadQuickAnswer(e.target.value);
  };

  const handleQuickAnswersClick = (value) => {
    setInputMessage(value);
    setTypeBar(false);
  };

  const handleAddEmoji = (e) => {
    let emoji = e.native;
    setInputMessage((prevState) => prevState + emoji);
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) {
      return;
    }
    const selectedMedias = Array.from(e.target.files);
    setMedias(selectedMedias);
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) {
      setMedias([e.clipboardData.files[0]]);
    }
  };

  const handleUploadMedia = async (e) => {
    setLoading(true);
    e.preventDefault();

    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach((media) => {
      formData.append("medias", media);
      formData.append("body", media.name);
    });

    try {
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setLoading(false);
    setMedias([]);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body: signMessage
        ? `*${user?.name}:*\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
      await api.post(`/messages/${ticketId}`, message);
    } catch (err) {
      toastError(err);
    }

    setInputMessage("");
    setShowEmoji(false);
    setLoading(false);
    setReplyingMessage(null);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      const audioRecorder = await initRecorder();
      if (!audioRecorder) {
        throw new Error("Recorder not available");
      }
      await audioRecorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", {
          params: { searchParam: inputMessage.substring(1) },
        });
        setQuickAnswer(data.quickAnswers);
        if (data.quickAnswers.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
    }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      const audioRecorder = await initRecorder();
      if (!audioRecorder) {
        throw new Error("Recorder not available");
      }
      const [, blob] = await audioRecorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const ext = blob.type.includes("ogg")
        ? "ogg"
        : blob.type.includes("webm")
        ? "webm"
        : blob.type.includes("mp4")
        ? "m4a"
        : "mp3";
      const filename = `${new Date().getTime()}.${ext}`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      const audioRecorder = await initRecorder();
      if (audioRecorder) {
        await audioRecorder.stop().getMp3();
      }
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  const renderReplyingMessage = (message) => {
    return (
      <div className="flex items-center w-full px-4 pt-2 bg-dark-100 dark:bg-dark-900">
        <div className="flex-1 flex rounded-lg overflow-hidden bg-white dark:bg-dark-800 mr-2">
          <span
            className={clsx(
              "w-1 flex-none",
              message.fromMe ? "bg-primary-400" : "bg-success-500"
            )}
          />
          <div className="p-2 text-sm overflow-hidden">
            {!message.fromMe && (
              <span className="text-primary-500 font-medium block text-xs">
                {message.contact?.name}
              </span>
            )}
            <span className="text-dark-600 dark:text-dark-400 line-clamp-2 text-xs">
              {message.body}
            </span>
          </div>
        </div>
        <button
          onClick={() => setReplyingMessage(null)}
          disabled={loading || ticketStatus !== "open"}
          className="p-2 text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 transition-colors"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>
    );
  };

  if (medias.length > 0) {
    return (
      <div className="flex items-center justify-between px-4 py-3 bg-dark-100 dark:bg-dark-900 border-t border-dark-200 dark:border-dark-700">
        <button
          onClick={() => setMedias([])}
          className="p-2 text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 transition-colors"
        >
          <CancelIcon className="w-6 h-6" />
        </button>

        {loading ? (
          <SpinnerIcon className="w-6 h-6 text-primary-500" />
        ) : (
          <span className="text-sm text-dark-600 dark:text-dark-400 truncate max-w-[200px]">
            {medias[0]?.name}
          </span>
        )}

        <button
          onClick={handleUploadMedia}
          disabled={loading}
          className="p-2 text-primary-500 hover:text-primary-600 transition-colors disabled:opacity-50"
        >
          <SendIcon className="w-6 h-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="bg-dark-100 dark:bg-dark-900 border-t border-dark-200 dark:border-dark-700 md:relative fixed bottom-0 left-0 right-0 md:left-auto md:right-auto">
      {replyingMessage && renderReplyingMessage(replyingMessage)}

      <div className="flex items-center gap-2 px-3 py-2">
        {/* Emoji Button - Desktop */}
        <div className="hidden md:block relative">
          <button
            onClick={() => setShowEmoji((prev) => !prev)}
            disabled={loading || recording || ticketStatus !== "open"}
            className="p-2 text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 transition-colors disabled:opacity-50"
          >
            <EmojiIcon className="w-6 h-6" />
          </button>
          {showEmoji && (
            <div className="absolute bottom-12 left-0 z-50">
              <div
                className="fixed inset-0"
                onClick={() => setShowEmoji(false)}
              />
              <div className="relative">
                <Picker
                  perLine={16}
                  showPreview={false}
                  showSkinTones={false}
                  onSelect={handleAddEmoji}
                />
              </div>
            </div>
          )}
        </div>

        {/* Attach Button - Desktop */}
        <div className="hidden md:block">
          <input
            multiple
            type="file"
            id="upload-button"
            disabled={loading || recording || ticketStatus !== "open"}
            className="hidden"
            onChange={handleChangeMedias}
          />
          <label htmlFor="upload-button">
            <button
              as="span"
              disabled={loading || recording || ticketStatus !== "open"}
              className="p-2 text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 transition-colors disabled:opacity-50 cursor-pointer"
              onClick={() => document.getElementById("upload-button").click()}
            >
              <AttachIcon className="w-6 h-6" />
            </button>
          </label>
        </div>

        {/* Sign Message Toggle - Desktop */}
        <label className="hidden md:flex items-center gap-2 text-xs text-dark-500 cursor-pointer">
          <input
            type="checkbox"
            checked={signMessage}
            onChange={(e) => setSignMessage(e.target.checked)}
            className="w-4 h-4 rounded border-dark-300 text-primary-500 focus:ring-primary-500"
          />
          {i18n.t("messagesInput.signMessage")}
        </label>

        {/* Message Input */}
        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            rows={1}
            placeholder={
              ticketStatus === "open"
                ? i18n.t("messagesInput.placeholderOpen")
                : i18n.t("messagesInput.placeholderClosed")
            }
            value={inputMessage}
            onChange={handleChangeInput}
            disabled={recording || loading || ticketStatus !== "open"}
            onPaste={(e) => ticketStatus === "open" && handleInputPaste(e)}
            onKeyPress={(e) => {
              if (loading || e.shiftKey) return;
              if (e.key === "Enter") {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            className="w-full px-4 py-2 bg-white dark:bg-dark-800 border border-dark-200 dark:border-dark-700 rounded-full text-sm text-dark-900 dark:text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 resize-none max-h-32 disabled:opacity-50"
            style={{ minHeight: "40px" }}
          />

          {/* Quick Answers Dropdown */}
          {typeBar && (
            <ul className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-dark-800 border border-dark-200 dark:border-dark-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {quickAnswers.map((value, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleQuickAnswersClick(value.message)}
                    className="w-full text-left px-4 py-2 text-sm text-dark-700 dark:text-dark-300 hover:bg-dark-50 dark:hover:bg-dark-700 truncate"
                  >
                    <span className="text-primary-500 font-medium">{value.shortcut}</span>
                    <span className="mx-2">-</span>
                    <span>{value.message}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Send / Record Buttons */}
        {inputMessage ? (
          <button
            onClick={handleSendMessage}
            disabled={loading}
            className="p-2 text-primary-500 hover:text-primary-600 transition-colors disabled:opacity-50"
          >
            <SendIcon className="w-6 h-6" />
          </button>
        ) : recording ? (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancelAudio}
              disabled={loading}
              className="p-2 text-danger-500 hover:text-danger-600 transition-colors"
            >
              <CancelIcon className="w-6 h-6" />
            </button>
            {loading ? (
              <SpinnerIcon className="w-6 h-6 text-primary-500" />
            ) : (
              <RecordingTimer />
            )}
            <button
              onClick={handleUploadAudio}
              disabled={loading}
              className="p-2 text-success-500 hover:text-success-600 transition-colors"
            >
              <CheckCircleIcon className="w-6 h-6" />
            </button>
          </div>
        ) : (
          <button
            onClick={handleStartRecording}
            disabled={loading || ticketStatus !== "open"}
            className="p-2 text-dark-400 hover:text-dark-600 dark:hover:text-dark-300 transition-colors disabled:opacity-50"
          >
            <MicIcon className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
};

export default MessageInput;
